import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import type { Store } from '@warp-drive/core';
import type { TasActor } from '../utils/tas-actor.ts';
import { getSingleValueForTasField } from '../utils/tuition-assistance/fields.ts';
import { saveRecord, destroyRecordViaBuilder } from './save-record.ts';
import type {
  TASApplicationModelFieldsSignature,
  TasApplication,
} from '../data/resources/tas-application.type';
import type { TasApplicationExtension } from '../data/resources/tas-application.ext';
import type { Tag as TagModel } from '../data/resources/tag.type';
import type { TasAppHistory } from '../types/tuition-assistance';

/**
 * TasApplication ACTION request builders (wave 1.5).
 *
 * Replaces the duplicated custom action methods on the near-identical
 * `tio-employee/app/adapters/tas-application.ts` and
 * `tio-admin/app/adapters/tas-application.ts`. The two adapter copies reduce to
 * two seams, BOTH pushed to the call site (not baked in here):
 *
 * - **actor `by` (state transitions)** — both adapters attach
 *   `by: { name, userId, actingWithRole }` to every state-transition body. They
 *   only differ in how `actingWithRole` is resolved (employee reads
 *   `sessionContext.currentRole`; admin applies the `isTasTioApprover`
 *   heuristic). That resolution now lives in `tio-data/utils/tas-actor`
 *   (`buildTasEmployeeActor` / `buildTasAdminActor` / `buildTasActorFromSession`);
 *   each call site builds a `TasActor` and hands it in. These builders nest it
 *   under `by` byte-identically.
 * - **demoMode (employee seam B)** — the employee `getHistory` consulted
 *   `demoMode.demoJsonFor(url, 'GET')` BEFORE the network because its raw
 *   `this.ajax` bypassed the request-manager demo handler. Routing the call site
 *   through `store.request(...)` honours the demo seam natively (the terminal
 *   `Fetch` handler's `window.fetch` is intercepted by `DemoModeService`), so a
 *   byte-identical URL preserves demo behaviour with NO explicit guard. The
 *   demo guard therefore stays OFF this builder — `historyRequest` emits only
 *   the raw GET.
 *
 * EMBER DATA `data` SEMANTICS — every legacy method issued
 * `this.ajax(url, VERB, { data: <body> })`. Ember Data's `ajax` `data` option IS
 * the JSON request body (it is NOT wrapped in a JSON:API `{ data }` envelope), so
 * the wire body equals the inner `<body>` object verbatim. Bodies below are
 * therefore the literal `data` objects the adapters sent.
 *
 * BODY CASING — preserved EXACTLY:
 * - `insert-reviewer` sends a KEBAB-case `'employee-id'` key beside camelCase
 *   `ordinal`.
 * - `create-card-holder` sends a flat snake_case body
 *   `{ legal_first_name, legal_last_name, dob, agreement_id }`.
 *
 * NO `_changelog` HERE — none of these custom action endpoints carry the
 * `adapterOptions.changelog` seam. `changelog` is a `record.save()` concern (the
 * application serializer injects `data.attributes._changelog` on a CRUD PATCH);
 * it belongs with `saveExistingRecord` / the CRUD reads-file path, NOT these
 * action builders. The convert agent handles changelog at the `save()` call
 * sites (approval-footer.gts:1300, application-information.gts:614) — see the
 * inventory's `adapterOptions.changelog` section. Do NOT add `_changelog` here.
 *
 * NOTE: the sibling `tio-data/requests/tas-application.ts` (reads/CRUD + an
 * earlier partial set of action builders from the consolidation) is the READS
 * file. These action builders live HERE; the convert agent reconciles any
 * overlap (e.g. an earlier `transitionTasApplicationRequest`) at conversion
 * time — see the report. Do NOT clobber the reads file.
 *
 * TODO(wave 3 — admin): `triggerRecommendation` is ADMIN-ONLY (no employee
 * adapter copy):
 *   POST /tas-applications/:id/recommend  body `{ application_id: id }` (snake_case)
 *   `this.ajax(url, 'POST', { data: { application_id: tasApplication.id } })`
 * Its only call site is in the tio-admin `app/` tree; it converts when the admin
 * call sites convert in wave 3. Do NOT add it here pre-emptively.
 */

const tasApplicationsUrlOptions: QueryUrlOptions = {
  identifier: { type: 'tas-applications' },
  op: 'query',
  resourcePath: 'tas-applications',
};

function tasApplicationsUrl(path?: string): string {
  const base = buildBaseURL(tasApplicationsUrlOptions);
  return path ? `${base}/${path.replace(/^\/+/, '')}` : base;
}

/* -------------------------------------------------------------------------- */
/* State transitions (PATCH /transition/state/<event>)                        */
/* -------------------------------------------------------------------------- */

/**
 * Application-field snapshot the legacy `triggerActionAndReload` helper read off
 * `tasApplication.fields` and sent as transition metadata. Resolved at the call
 * site (via `getSingleValueForTasField`) and passed in; when omitted, each field
 * falls back to the adapter's default (`null` for totals, `false` for the waive
 * flags) — byte-identical to the adapter's `... || null` / `... || false`
 * resolution. Atomic field persistence uses `TasApplicationSubmission` below.
 */
export interface TasApplicationTransitionFieldData {
  preApprovedTotal: number | null;
  finalApprovedTotal: number | null;
  waiveEvidenceAttachmentRequirement: boolean;
  waiveEvidenceApprovalRequirement: boolean;
}

export interface TasApplicationSubmissionFieldValues {
  SEMESTER_CODE?: string;
  PAYMENT_TYPE_FOR_DIRECT_PAY?: string;
  PRE_APPROVED_TOTAL?: number | null;
  FINAL_APPROVED_TOTAL?: number | null;
  CARD_PRE_APPROVED_TOTAL?: number | null;
  CARD_FINAL_APPROVED_TOTAL?: number | null;
}

export interface TasApplicationSubmission {
  /** Raw values for the server's submission-time application-field whitelist. */
  fieldValues: TasApplicationSubmissionFieldValues;
  /** Persisted template-condition ids the employee accepted for this submission. */
  agreementConditionIds: string[];
}

/**
 * Builds the {@link TasApplicationTransitionFieldData} snapshot off an
 * application's `fields`, byte-identical to what
 * `TasApplicationAdapter#triggerActionAndReload` (BOTH apps) read before every
 * state-transition:
 *
 *   const preApprovedTotal =
 *     getSingleValueForTasField('PRE_APPROVED_TOTAL', fields) || null;
 *   const finalApprovedTotal =
 *     getSingleValueForTasField('FINAL_APPROVED_TOTAL', fields) || null;
 *   const waiveEvidenceAttachmentRequirement =
 *     getSingleValueForTasField('WAIVE_EVIDENCE_ATTACHMENT_REQUIREMENT', fields) || false;
 *   const waiveEvidenceApprovalRequirement =
 *     getSingleValueForTasField('WAIVE_EVIDENCE_APPROVAL_REQUIREMENT', fields) || false;
 *
 * The `|| null` / `|| false` (NOT `??`) is preserved exactly: a falsy field
 * value (e.g. `0`, `''`) collapses to the adapter's fallback, matching the wire
 * the legacy adapter sent. Call sites that resolve transitions dynamically use
 * this helper so the four-field body stays identical to the adapter.
 */
export function buildTasTransitionFieldData(
  fields: TASApplicationModelFieldsSignature
): TasApplicationTransitionFieldData {
  return {
    preApprovedTotal:
      (getSingleValueForTasField('PRE_APPROVED_TOTAL', fields) as number | null) || null,
    finalApprovedTotal:
      (getSingleValueForTasField('FINAL_APPROVED_TOTAL', fields) as number | null) || null,
    waiveEvidenceAttachmentRequirement:
      (getSingleValueForTasField('WAIVE_EVIDENCE_ATTACHMENT_REQUIREMENT', fields) as
        boolean | null) || false,
    waiveEvidenceApprovalRequirement:
      (getSingleValueForTasField('WAIVE_EVIDENCE_APPROVAL_REQUIREMENT', fields) as
        boolean | null) || false,
  };
}

/** TAS state-transition events that go through the full transition body. */
export type TasApplicationTransitionEvent =
  | 'define-courses'
  | 'request-courses-approval'
  | 'approve-courses'
  | 'reject-courses'
  | 'decline-courses'
  | 'request-evidence-approval'
  | 'approve-evidence'
  | 'reject-evidence'
  | 'decline-evidence'
  | 'abandon';

/**
 * Maps a legacy `TasApplicationAdapter` state-transition method name to its
 * `transition/state/<event>` event. Call sites that resolved the adapter method
 * dynamically (e.g. `adapter[this.adapterMethod](application, comment)`) now
 * resolve the event through this map, then route through
 * {@link transitionTasApplicationRequest}. Each entry is the exact endpoint the
 * legacy method passed to `triggerActionAndReload`.
 */
export const TAS_TRANSITION_EVENT_FOR_METHOD: Record<string, TasApplicationTransitionEvent> = {
  requestCoursesApproval: 'request-courses-approval',
  approveCourses: 'approve-courses',
  rejectCourses: 'reject-courses',
  requestChanges: 'decline-courses',
  requestCourseEvidence: 'request-evidence-approval',
  approveCourseEvidence: 'approve-evidence',
  rejectCourseEvidence: 'reject-evidence',
  requestCourseEvidenceChanges: 'decline-evidence',
  abandonCourse: 'abandon',
};

/**
 * Generic builder for PATCH /tas-applications/:id/transition/state/:event.
 *
 * Mirrors `TasApplicationAdapter#triggerActionAndReload` (BOTH apps), which built
 * the URL via `this.buildURL('tas-application', id) + actionEndpoint` and sent:
 *   this.ajax(url, 'PATCH', {
 *     data: {
 *       note: comment,
 *       preApprovedTotal, finalApprovedTotal,
 *       waiveEvidenceAttachmentRequirement, waiveEvidenceApprovalRequirement,
 *       by: { name, userId, actingWithRole },
 *     },
 *   })
 * then awaited `tasApplication.reload()` — the reload is now an explicit
 * follow-up at the call site (or via the convert agent's reload fold-in).
 *
 * `note` defaults to `''` (the adapter's `comment = ''` default). `fieldData`
 * defaults each field to the adapter fallback (`null` totals / `false` flags).
 */
function buildTransitionTasApplicationRequest(
  applicationId: string,
  event: TasApplicationTransitionEvent,
  actor: TasActor,
  note = '',
  fieldData?: TasApplicationTransitionFieldData,
  submission?: TasApplicationSubmission
): RequestInfo {
  return {
    url: tasApplicationsUrl(`${applicationId}/transition/state/${event}`),
    method: 'PATCH',
    body: JSON.stringify({
      note,
      preApprovedTotal: fieldData?.preApprovedTotal ?? null,
      finalApprovedTotal: fieldData?.finalApprovedTotal ?? null,
      waiveEvidenceAttachmentRequirement: fieldData?.waiveEvidenceAttachmentRequirement ?? false,
      waiveEvidenceApprovalRequirement: fieldData?.waiveEvidenceApprovalRequirement ?? false,
      by: actor,
      ...(submission ? { submission } : {}),
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

export function transitionTasApplicationRequest(
  applicationId: string,
  event: TasApplicationTransitionEvent,
  actor: TasActor,
  note = '',
  fieldData?: TasApplicationTransitionFieldData
): RequestInfo {
  return buildTransitionTasApplicationRequest(applicationId, event, actor, note, fieldData);
}

/**
 * PATCH /tas-applications/:id/transition/state/define-courses — reopens a
 * returned application for correction. The AASM event accepts CREATED,
 * COURSES_NOT_APPROVED and EVIDENCE_NOT_APPROVED (tas_application.rb), landing
 * back at DEFINE_COURSES, from which the wizard's normal submits are legal
 * again. Hard rejects (COURSES_REJECTED / EVIDENCE_REJECTED) are not accepted —
 * terminal by design.
 */
export const defineCoursesRequest = (
  applicationId: string,
  actor: TasActor,
  note = '',
  fieldData?: TasApplicationTransitionFieldData
): RequestInfo =>
  transitionTasApplicationRequest(applicationId, 'define-courses', actor, note, fieldData);

/**
 * PATCH /tas-applications/:id/transition/state/request-courses-approval.
 *
 * `submission` lets the employee's final guided submit persist its whitelisted
 * application fields, accepted-condition agreements, and state transition in
 * the endpoint's one transaction. Omit it for the generic transition-only wire
 * contract used by other callers.
 */
export const requestCoursesApprovalRequest = (
  applicationId: string,
  actor: TasActor,
  note = '',
  fieldData?: TasApplicationTransitionFieldData,
  submission?: TasApplicationSubmission
): RequestInfo =>
  buildTransitionTasApplicationRequest(
    applicationId,
    'request-courses-approval',
    actor,
    note,
    fieldData,
    submission
  );

/** PATCH /tas-applications/:id/transition/state/approve-courses (adapter `approveCourses`). */
export const approveCoursesRequest = (
  applicationId: string,
  actor: TasActor,
  note = '',
  fieldData?: TasApplicationTransitionFieldData
): RequestInfo =>
  transitionTasApplicationRequest(applicationId, 'approve-courses', actor, note, fieldData);

/** PATCH /tas-applications/:id/transition/state/decline-courses (adapter `requestChanges`). */
export const declineCoursesRequest = (
  applicationId: string,
  actor: TasActor,
  note = '',
  fieldData?: TasApplicationTransitionFieldData
): RequestInfo =>
  transitionTasApplicationRequest(applicationId, 'decline-courses', actor, note, fieldData);

/** PATCH /tas-applications/:id/transition/state/request-evidence-approval (adapter `requestCourseEvidence`). */
export const requestEvidenceApprovalRequest = (
  applicationId: string,
  actor: TasActor,
  note = '',
  fieldData?: TasApplicationTransitionFieldData
): RequestInfo =>
  transitionTasApplicationRequest(
    applicationId,
    'request-evidence-approval',
    actor,
    note,
    fieldData
  );

/** PATCH /tas-applications/:id/transition/state/approve-evidence (adapter `approveCourseEvidence`). */
export const approveEvidenceRequest = (
  applicationId: string,
  actor: TasActor,
  note = '',
  fieldData?: TasApplicationTransitionFieldData
): RequestInfo =>
  transitionTasApplicationRequest(applicationId, 'approve-evidence', actor, note, fieldData);

/** PATCH /tas-applications/:id/transition/state/decline-evidence (adapter `requestCourseEvidenceChanges`). */
export const declineEvidenceRequest = (
  applicationId: string,
  actor: TasActor,
  note = '',
  fieldData?: TasApplicationTransitionFieldData
): RequestInfo =>
  transitionTasApplicationRequest(applicationId, 'decline-evidence', actor, note, fieldData);

/** PATCH /tas-applications/:id/transition/state/abandon (adapter `abandonCourse`). */
export const abandonCourseRequest = (
  applicationId: string,
  actor: TasActor,
  note = '',
  fieldData?: TasApplicationTransitionFieldData
): RequestInfo => transitionTasApplicationRequest(applicationId, 'abandon', actor, note, fieldData);

/**
 * PATCH /tas-applications/:id/transition/state/rescind  (NO BODY)
 *
 * Mirrors `TasApplicationAdapter#rescindLastApproverAction` (BOTH apps), which
 * built the URL via `this.buildURL('tas-application', id) + '/transition/state/rescind'`
 * and sent `this.ajax(url, 'PATCH')` — NO body, NO actor (it does NOT route
 * through `triggerActionAndReload`).
 */
export function rescindLastApproverActionRequest(applicationId: string): RequestInfo {
  return {
    url: tasApplicationsUrl(`${applicationId}/transition/state/rescind`),
    method: 'PATCH',
    cacheOptions: { [SkipCache]: true },
  };
}

/* -------------------------------------------------------------------------- */
/* Money operations                                                           */
/* -------------------------------------------------------------------------- */

/**
 * PATCH /tas-applications/:id/update-approved-total
 * body `{ amount, reason, additionalFieldValues? }`
 *
 * Mirrors `TasApplicationAdapter#updateApprovedTotal` (BOTH apps), which sent
 * `this.ajax(url, 'PATCH', { data: { amount, reason, additionalFieldValues } })`.
 * The employee adapter typed `additionalFieldValues?` (optional, no default →
 * `undefined` → JSON.stringify DROPS the key → body `{ amount, reason }`); the
 * admin adapter defaulted it to `{}` (→ body `{ amount, reason, additionalFieldValues: {} }`).
 * Parameterized as OPTIONAL here: omitted from the wire when `undefined`
 * (employee parity), present when the caller passes `{}` or values (admin
 * parity). The legacy method then awaited `reload({ include: 'tas-assistances' })`.
 */
export function updateApprovedTotalRequest(
  applicationId: string,
  amount: unknown,
  reason: string,
  additionalFieldValues?: Record<string, unknown>
): RequestInfo {
  return {
    url: tasApplicationsUrl(`${applicationId}/update-approved-total`),
    method: 'PATCH',
    body: JSON.stringify({ amount, reason, additionalFieldValues }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /tas-applications/:id/request-repayment
 * body `{ amount, reason }`
 *
 * Mirrors `TasApplicationAdapter#requestRepayment` (BOTH apps):
 * `this.ajax(url, 'PATCH', { data: { amount, reason } })`, then
 * `reload({ include: 'tas-assistances,tas-program-instance.employee.tas-participant' })`.
 */
export function requestRepaymentRequest(
  applicationId: string,
  amount: unknown,
  reason: string
): RequestInfo {
  return {
    url: tasApplicationsUrl(`${applicationId}/request-repayment`),
    method: 'PATCH',
    body: JSON.stringify({ amount, reason }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /tas-applications/:id/request-payment
 * body `{ amount, reason }`
 *
 * Mirrors `TasApplicationAdapter#requestPayment` (BOTH apps):
 * `this.ajax(url, 'PATCH', { data: { amount, reason } })`, then
 * `reload({ include: 'tas-assistances,tas-program-instance.employee.tas-participant' })`.
 */
export function requestPaymentRequest(
  applicationId: string,
  amount: unknown,
  reason: string
): RequestInfo {
  return {
    url: tasApplicationsUrl(`${applicationId}/request-payment`),
    method: 'PATCH',
    body: JSON.stringify({ amount, reason }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /tas-applications/:id/record-repayment
 * body `{ amount, reason }`
 *
 * Mirrors `TasApplicationAdapter#recordRepayment` (BOTH apps):
 * `this.ajax(url, 'PATCH', { data: { amount, reason } })`, then
 * `reload({ include: 'tas-assistances,tas-program-instance.employee.tas-participant' })`.
 */
export function recordRepaymentRequest(
  applicationId: string,
  amount: number,
  reason: string
): RequestInfo {
  return {
    url: tasApplicationsUrl(`${applicationId}/record-repayment`),
    method: 'PATCH',
    body: JSON.stringify({ amount, reason }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /tas-applications/:id/forgive-repayment
 * body `{ amount, reason }`
 *
 * Mirrors `TasApplicationAdapter#forgiveRepayment` (BOTH apps):
 * `this.ajax(url, 'PATCH', { data: { amount, reason } })`, then
 * `reload({ include: 'tas-assistances,tas-program-instance.employee.tas-participant' })`.
 */
export function forgiveRepaymentRequest(
  applicationId: string,
  amount: number,
  reason: string
): RequestInfo {
  return {
    url: tasApplicationsUrl(`${applicationId}/forgive-repayment`),
    method: 'PATCH',
    body: JSON.stringify({ amount, reason }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /tas-applications/:id/cancel-repayment
 * body `{ reason }`  (reason ONLY — no amount)
 *
 * Mirrors `TasApplicationAdapter#cancelRepayment` (BOTH apps):
 * `this.ajax(url, 'PATCH', { data: { reason } })`, then
 * `reload({ include: 'tas-assistances,tas-program-instance.employee.tas-participant' })`.
 */
export function cancelRepaymentRequest(applicationId: string, reason: string): RequestInfo {
  return {
    url: tasApplicationsUrl(`${applicationId}/cancel-repayment`),
    method: 'PATCH',
    body: JSON.stringify({ reason }),
    cacheOptions: { [SkipCache]: true },
  };
}

/** The legacy money-operation adapter methods the request-summary screens dispatch by name. */
export type TasMoneyTransactionMethod =
  | 'requestRepayment'
  | 'requestPayment'
  | 'updateApprovedTotal'
  | 'forgiveRepayment'
  | 'recordRepayment'
  | 'cancelRepayment';

/**
 * Dispatches a money-operation adapter method (resolved dynamically at the call
 * site via the screen's `actionMap`) to the matching request builder. The
 * request-summary screens (`request-summary.gts`,
 * `financial-details-request-summary.gts`) all call
 * `adapter[this.adapterMethod](application, amount, reason)`; this preserves
 * that single dynamic seam while routing through `store.request`.
 *
 * Wire is byte-identical to the per-method builders:
 * - `cancelRepayment` sends `{ reason }` only (amount ignored — reason-only wire).
 * - `updateApprovedTotal` is called WITHOUT `additionalFieldValues` here, so the
 *   key drops from the body (employee parity — the request-summary screens never
 *   passed field values for the `editApprovedTotal` action).
 */
export function tasMoneyTransactionRequest(
  method: TasMoneyTransactionMethod,
  applicationId: string,
  amount: number,
  reason: string
): RequestInfo {
  switch (method) {
    case 'requestRepayment':
      return requestRepaymentRequest(applicationId, amount, reason);
    case 'requestPayment':
      return requestPaymentRequest(applicationId, amount, reason);
    case 'updateApprovedTotal':
      return updateApprovedTotalRequest(applicationId, amount, reason);
    case 'forgiveRepayment':
      return forgiveRepaymentRequest(applicationId, amount, reason);
    case 'recordRepayment':
      return recordRepaymentRequest(applicationId, amount, reason);
    case 'cancelRepayment':
      return cancelRepaymentRequest(applicationId, reason);
  }
}

/* -------------------------------------------------------------------------- */
/* Reviewers / waivers / history / card-holder                               */
/* -------------------------------------------------------------------------- */

/**
 * PATCH /tas-applications/:id/insert-reviewer
 * body `{ 'employee-id', ordinal }`  (KEBAB-case 'employee-id' key — preserve)
 *
 * Mirrors `TasApplicationAdapter#insertReviewer` (BOTH apps):
 * `this.ajax(url, 'PATCH', { data: { 'employee-id': employeeId, ordinal } })`,
 * then `reload()`. (The employee adapter additionally guarded required params;
 * that guard is a call-site concern.)
 */
export function insertReviewerRequest(
  applicationId: string,
  employeeId: number,
  ordinal: number
): RequestInfo {
  return {
    url: tasApplicationsUrl(`${applicationId}/insert-reviewer`),
    method: 'PATCH',
    body: JSON.stringify({ 'employee-id': employeeId, ordinal }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /tas-applications/:id/waive-evidence-approval  (NO BODY)
 *
 * Mirrors `TasApplicationAdapter#waiveEvidenceApproval` (BOTH apps), which built
 * the URL via `this.buildURL('tas-application', id) + '/waive-evidence-approval'`
 * and sent `this.ajax(url, 'PATCH')` — NO body.
 */
export function waiveEvidenceApprovalRequest(applicationId: string): RequestInfo {
  return {
    url: tasApplicationsUrl(`${applicationId}/waive-evidence-approval`),
    method: 'PATCH',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * GET /tas-applications/:id/history
 *
 * Mirrors `TasApplicationAdapter#getHistory` (BOTH apps), which built the URL via
 * `this.buildURL('tas-application', id) + '/history'` and sent
 * `this.ajax(url, 'GET')` (raw JSON response). The EMPLOYEE adapter additionally
 * consulted `demoMode.demoJsonFor(url, 'GET')` first; that demo seam is honoured
 * natively once routed through `store.request` (see module docblock) — NO guard
 * here.
 */
export function historyRequest(applicationId: string): RequestInfo {
  return {
    url: tasApplicationsUrl(`${applicationId}/history`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Fetches transition history and assigns it to the application. The explicit store supports owner-
 * free resource callers.
 */
export async function loadTasApplicationHistory(
  store: Store,
  application: TasApplication
): Promise<TasAppHistory> {
  const response = await store.request(historyRequest(application.id as string));
  const history = (response.content as { data: TasAppHistory }).data;
  application.history = history;
  return history;
}

/** Adds or removes the In Collections tag. The explicit store supports owner-free resource callers. */
export async function toggleTasApplicationInCollections(
  store: Store,
  application: TasApplicationExtension
): Promise<void | TagModel> {
  if (application.isInCollections) {
    const tag = application.tags.find((t) => t.name === 'In Collections');
    if (tag) {
      await destroyRecordViaBuilder(store, tag);
    }
  } else {
    const tag = store.createRecord('tag', {
      name: 'In Collections',
      taggable: application,
    }) as TagModel;
    await saveRecord(store, tag);
    return tag;
  }
}

/** The flat snake_case body for `create-card-holder`. */
export interface CreateCardHolderBody {
  legal_first_name: string;
  legal_last_name: string;
  dob: string;
  agreement_id: string;
}

/**
 * POST /tas-applications/:id/create-card-holder
 * body `{ legal_first_name, legal_last_name, dob, agreement_id }`  (snake_case)
 *
 * Mirrors `TasApplicationAdapter#createCardHolder` (EMPLOYEE adapter ONLY — the
 * admin adapter has no copy), which built the URL via
 * `this.buildURL('tas-application', id) + '/create-card-holder'` and sent
 * `this.ajax(url, 'POST', { data: payload })` where `payload` is the snake_case
 * object below. The snake_case key casing is preserved EXACTLY. The legacy method
 * then `store.pushPayload(response)` + linked the resulting pcs-card-holder to the
 * employee belongsTo — those are response/cache concerns for the convert agent;
 * this builder emits only the outbound POST.
 */
export function createCardHolderRequest(
  applicationId: string,
  payload: CreateCardHolderBody
): RequestInfo {
  return {
    url: tasApplicationsUrl(`${applicationId}/create-card-holder`),
    method: 'POST',
    body: JSON.stringify({
      legal_first_name: payload.legal_first_name,
      legal_last_name: payload.legal_last_name,
      dob: payload.dob,
      agreement_id: payload.agreement_id,
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * POST /tas-applications/:id/recommend  body `{ application_id: id }` (snake_case)
 * (admin-only surface)
 *
 * Mirrors `TasApplicationAdapter#triggerRecommendation` (tio-admin adapter only),
 * which built the URL via `this.buildURL('tas-application', tasApplication.id) +
 * '/recommend'` and sent `this.ajax(url, 'POST', { data: { application_id:
 * tasApplication.id } })`. The snake_case `application_id` key is preserved exactly.
 */
export function triggerRecommendationRequest(applicationId: string): RequestInfo {
  return {
    url: tasApplicationsUrl(`${applicationId}/recommend`),
    method: 'POST',
    body: JSON.stringify({ application_id: applicationId }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * POST /tas-applications/:id/create-pdf-asset  (NO BODY)
 * (admin-only surface)
 *
 * Support backdoor: re-enqueues the application's PDF-generation jobs (BCBSM
 * voucher / advancement document) when the automatic state-transition trigger
 * failed, so support no longer has to bounce the application between states.
 * The server enforces the company allowlist (`CreatePdfAssetPolicy`) and
 * responds 400 when PDF generation is not enabled for the company.
 */
export function createPdfAssetRequest(applicationId: string): RequestInfo {
  return {
    url: tasApplicationsUrl(`${applicationId}/create-pdf-asset`),
    method: 'POST',
    cacheOptions: { [SkipCache]: true },
  };
}
